import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  Printer,
  Package,
  Receipt,
  LoaderCircle,
  AlertCircle,
  Phone,
  Mail
} from "lucide-react";
import { useApi } from "../utils/Api";
import printSuccessImage from "../assets/print-success.png";
import SEO from "../components/SEO";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

export default function PrintOrderSuccess() {
  const { apiFetch } = useApi();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setError("Missing payment session.");
      setLoading(false);
      return;
    }

    let interval;

    const fetchOrder = async () => {
      try {
        const res = await apiFetch("verify_print_payment", {
          session_id: sessionId,
        });

        if (!res?.success) {
          throw new Error(res?.message || "Unable to load order.");
        }

        setOrder(res.data);

        if (res.data.payment_status === "paid" && interval) {
          clearInterval(interval);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
    // Poll every 3 seconds until paid
    interval = setInterval(fetchOrder, 3000);

    return () => clearInterval(interval);
  }, [sessionId, apiFetch]);

  const total = useMemo(
    () => money(order?.total_price),
    [order]
  );

  /* ========================= LOADING ========================= */
  if (loading) {
    return (
      <main className="min-h-screen bg-black pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-[2.5rem] bg-white p-12 text-center shadow-2xl">
          <LoaderCircle className="mx-auto h-12 w-12 animate-spin text-pink-500" />
          <p className="mt-6 text-gray-500 font-medium">Verifying your order...</p>
        </div>
      </main>
    );
  }

  /* ========================= ERROR ========================= */
  if (error || !order) {
    return (
      <main className="min-h-screen bg-black pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-[2.5rem] bg-white p-12 text-center shadow-2xl">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-6 text-2xl font-bold text-gray-900">Something went wrong</h1>
          <p className="mt-3 text-gray-500 leading-relaxed">{error}</p>
          <Link
            to="/printing"
            className="mt-8 inline-flex items-center justify-center rounded-2xl bg-black px-8 py-4 text-sm font-bold text-white transition-transform active:scale-95"
          >
            Back to Printing
          </Link>
        </div>
      </main>
    );
  }

  /* ========================= PENDING ========================= */
  if (order.payment_status !== "paid") {
    return (
      <main className="min-h-screen bg-black pt-28 px-4 py-10">
        <div className="mx-auto max-w-[520px] rounded-[2.5rem] bg-white p-12 text-center shadow-2xl">
          <LoaderCircle className="mx-auto h-12 w-12 animate-spin text-pink-500" />
          <h1 className="mt-8 text-3xl font-bold text-gray-900">Processing Payment</h1>
          <p className="mt-3 text-gray-500 leading-relaxed">
            We're confirming your print order. Please don't refresh the page.
          </p>
          {order.order_ref && (
            <div className="mt-6 rounded-2xl bg-gray-50 p-4 border border-gray-100">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">Order Reference</p>
              <p className="mt-1 font-mono text-lg font-bold text-black">{order.order_ref}</p>
            </div>
          )}
        </div>
      </main>
    );
  }

  /* ========================= SUCCESS ========================= */
  return (
    <>
      <SEO title="Order Success - Kinews Studio" />
      <main className="min-h-screen bg-black pt-28 px-4 pb-20">
        <div className="mx-auto max-w-[520px] overflow-hidden rounded-[2.5rem] bg-white shadow-2xl">
          
          {/* HEADER */}
          <div className="px-8 pb-10 pt-12 text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
              <CheckCircle2 size={48} className="text-green-500" />
            </div>

            <h1 className="text-3xl font-black tracking-tight text-gray-900">
              Order Placed!
            </h1>

            <p className="mt-3 text-gray-500 font-medium">
              Thank you for choosing Kinews Studio.
            </p>

            <div className="mt-6 inline-block rounded-2xl bg-gray-50 px-6 py-3 border border-gray-100">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-black">Ref Number</p>
              <p className="mt-1 font-mono text-lg font-bold text-black">{order.order_ref}</p>
            </div>
          </div>

          {/* IMAGE */}
          <div className="h-48 w-full overflow-hidden">
            <img
              src={printSuccessImage}
              alt="Printing success"
              className="h-full w-full object-cover"
            />
          </div>

          {/* DETAILS */}
          <div className="px-8 py-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 rounded-lg bg-pink-500/10 flex items-center justify-center text-pink-600">
                <Printer size={18} />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Order Details</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4 rounded-2xl bg-gray-50 p-4 border border-gray-100">
                <Package size={20} className="mt-1 text-gray-400" />
                <div>
                  <p className="text-sm font-bold text-gray-900">{order.category}</p>
                  <p className="mt-1 text-xs font-medium text-gray-500 leading-relaxed">
                    {order.sub_label} | {order.group_label} | {order.sided === 'double' ? 'Double sided' : 'Single sided'} | {order.qty}units
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 rounded-2xl bg-gray-50 p-4 border border-gray-100">
                <Receipt size={20} className="mt-1 text-gray-400" />
                <div>
                  <p className="text-sm font-bold text-gray-900">Payment Status</p>
                  <p className="mt-1 text-xs font-medium text-gray-500">
                    <span className="text-black font-bold">{total}</span> successfully paid via Stripe.
                  </p>
                </div>
              </div>
            </div>

            {/* NEXT STEPS */}
            <div className="mt-10 pt-8 border-t border-gray-100">
              <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-4">What's Next?</h3>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pink-500 text-white text-xs font-bold shadow-lg shadow-pink-500/20">1</div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    A team member will reach out to you via <span className="font-bold text-black">{order.phone}</span> or <span className="font-bold text-black">{order.email}</span> to collect your design files.
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pink-500 text-white text-xs font-bold shadow-lg shadow-pink-500/20">2</div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Once the files are received, we will begin the printing process.
                  </p>
                </div>
              </div>
            </div>

            {/* CONTACT HELP */}
            <div className="mt-10 rounded-3xl bg-pink-500 p-8 text-white shadow-xl shadow-pink-500/20">
              <p className="text-xs font-black uppercase tracking-widest opacity-60">Need help?</p>
              <h4 className="mt-1 text-lg font-bold">Contact our support</h4>
              <div className="mt-6 space-y-3">
                <a href="tel:+447486160965" className="flex items-center gap-3 text-sm font-bold hover:opacity-80 transition">
                  <Phone size={16} /> +44 7486 160965
                </a>
                <a href="mailto:info@kinewsstudiolimited.co.uk" className="flex items-center gap-3 text-sm font-bold hover:opacity-80 transition">
                  <Mail size={16} /> info@kinewsstudiolimited.co.uk
                </a>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-col gap-3">
              <Link
                to="/printing"
                className="flex items-center justify-center rounded-2xl bg-black px-6 py-4 text-sm font-bold text-white transition hover:bg-gray-900 active:scale-95 shadow-xl"
              >
                Book another print
              </Link>
              <Link
                to="/"
                className="flex items-center justify-center rounded-2xl bg-gray-100 px-6 py-4 text-sm font-bold text-gray-600 transition hover:bg-gray-200 active:scale-95"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
